import { PillTone } from '../components/DashboardPrimitives'
import {
  ICheckoutSession,
  ManagedEvent,
  OpenBankingPaymentHistoryEntry,
  OpenBankingPaymentStatus,
  OpenBankingPaymentSummary,
  OpenBankingPaymentView
} from '../state/Radom'

/**
 * One vocabulary for the state of an open-banking payment, used everywhere a
 * payment is shown: search, lists, the payment drawer and the session page.
 *
 * A bank payment passes through several provider statuses. None of them is a
 * payment on its own; the payment is in exactly one state, and these are the
 * words for it.
 */
interface StatusPresentation {
  label: string
  tone: PillTone
  description: string
}

const STATUS_PRESENTATION: Record<OpenBankingPaymentStatus, StatusPresentation> = {
  awaitingCustomer: {
    label: 'Waiting for customer',
    tone: 'neutral',
    description: 'The customer has opened the bank payment but has not approved it yet. Nothing has been paid.'
  },
  notCompleted: {
    label: 'Not completed',
    tone: 'neutral',
    description: 'The customer did not approve the bank payment before the session expired. Nothing was paid.'
  },
  awaitingFunds: {
    label: 'Awaiting funds',
    tone: 'blue',
    description: 'The customer approved the payment at their bank. It is credited to your balance as soon as the transfer arrives.'
  },
  underReview: {
    label: 'Under review',
    tone: 'amber',
    description: 'The payment provider is checking this payment before releasing it. Most clear within minutes, some take a few days. It is credited once it clears, and you do not need to do anything.'
  },
  paid: {
    label: 'Paid',
    tone: 'green',
    description: 'Received and credited to your balance.'
  },
  cancelled: {
    label: 'Cancelled',
    tone: 'neutral',
    description: 'The bank payment was cancelled before it was approved. Nothing was paid.'
  },
  declined: {
    label: 'Declined',
    tone: 'red',
    description: 'The payment provider declined this payment after its checks. Nothing was credited to your balance, and any money the customer\'s bank had already sent is returned to them.'
  },
  failed: {
    label: 'Failed',
    tone: 'red',
    description: 'The bank transfer never arrived, so nothing was credited to your balance.'
  },
  unresolved: {
    label: 'Needs checking',
    tone: 'amber',
    description: 'This payment has not been settled in the time a bank payment takes, and nothing has been added to your balance for it. Contact support and we will check it with the payment provider.'
  },
  refundInProgress: {
    label: 'Refund in progress',
    tone: 'amber',
    description: 'A refund has been sent to the customer\'s bank account and is on its way.'
  },
  partiallyRefunded: {
    label: 'Partially refunded',
    tone: 'amber',
    description: 'Part of this payment has been refunded to the customer.'
  },
  refunded: {
    label: 'Refunded',
    tone: 'neutral',
    description: 'This payment has been refunded to the customer.'
  },
  refundFailed: {
    label: 'Refund failed',
    tone: 'red',
    description: 'The refund could not be delivered and was returned to your balance.'
  }
}

const UNKNOWN_STATUS: StatusPresentation = {
  label: 'Processing',
  tone: 'neutral',
  description: 'This payment is being processed.'
}

export const openBankingStatusPresentation = (status?: OpenBankingPaymentStatus | string): StatusPresentation =>
  (status && STATUS_PRESENTATION[status as OpenBankingPaymentStatus]) || UNKNOWN_STATUS

/** Whether a payment carries the full detail view, or only the summary a list row has. */
export const isOpenBankingPaymentView = (
  payment: OpenBankingPaymentView | OpenBankingPaymentSummary
): payment is OpenBankingPaymentView => 'history' in payment

/** The payment can still complete on its own: the customer has done their part. */
export const isOpenBankingPaymentInFlight = (status?: OpenBankingPaymentStatus | string): boolean =>
  status === 'awaitingFunds' || status === 'underReview'

/**
 * The status to show for a checkout session. A session whose bank payment is
 * still completing is not over, whatever its expiry time says.
 */
export const effectiveSessionStatus = <T extends string>(
  sessionStatus: T,
  bankPaymentStatus?: OpenBankingPaymentStatus | string
): T | 'pending' =>
  sessionStatus === 'expired' && isOpenBankingPaymentInFlight(bankPaymentStatus) ? 'pending' : sessionStatus

/** Nothing more will happen to the payment without a new action. */
export const isOpenBankingPaymentFinal = (status?: OpenBankingPaymentStatus | string): boolean =>
  !['awaitingCustomer', 'awaitingFunds', 'underReview', 'unresolved', 'refundInProgress'].includes(status ?? '')

const normalise = (value?: string): string => (value ?? '').trim().toLowerCase()

const isProviderRejection = (reason?: string): boolean => {
  const upper = (reason ?? '').toUpperCase()
  return upper.includes('AML') || upper.includes('REJECT') || upper.includes('FRAUD')
}

/**
 * An order that has been open for longer than this has lost an update: the
 * provider settles, declines or fails every order well within it.
 */
const IN_FLIGHT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000

const isLongAgo = (lastUpdate?: string): boolean => {
  if (!lastUpdate) return false
  const time = new Date(lastUpdate).getTime()
  return !Number.isNaN(time) && Date.now() - time > IN_FLIGHT_WINDOW_MS
}

/**
 * The merchant status for a provider status. The backend sends the status
 * itself; this exists for data that only carries the provider's word (older
 * API responses, and events stored before the status existed). `lastUpdate`
 * is when the payment last changed status, when that is known.
 */
export const openBankingStatusFromProvider = (
  providerStatus: string | undefined,
  reason: string | undefined,
  sessionClosed: boolean,
  lastUpdate?: string
): OpenBankingPaymentStatus => {
  switch (normalise(providerStatus).replace(/[\s-]/g, '_')) {
  case 'processing':
    return sessionClosed ? 'notCompleted' : 'awaitingCustomer'
  case 'waiting_for_payment':
  case 'authorized':
    return isLongAgo(lastUpdate) ? 'unresolved' : 'awaitingFunds'
  case 'finalizing':
    return isLongAgo(lastUpdate) ? 'unresolved' : 'underReview'
  case 'paid':
    return 'paid'
  case 'canceled':
  case 'cancelled':
    return isProviderRejection(reason) ? 'declined' : 'cancelled'
  case 'failed':
    return 'failed'
  case 'in_refund':
    return 'refundInProgress'
  case 'partially_refunded':
    return 'partiallyRefunded'
  case 'refunded':
    return 'refunded'
  case 'refund_failed':
    return 'refundFailed'
  default:
    return sessionClosed ? 'notCompleted' : 'awaitingCustomer'
  }
}

interface HistoryPresentation {
  title: string
  detail?: string
  tone: PillTone
}

const REASON_PRESENTATION: Record<string, HistoryPresentation> = {
  PAYMENT_INITIATED: { title: 'Customer started the bank payment', tone: 'neutral' },
  PIS_PAYMENT_SUCCEEDED: {
    title: 'Customer approved the payment at their bank',
    detail: 'The bank accepted the payment. Waiting for the transfer to arrive.',
    tone: 'blue'
  },
  CHECKOUT_COMPLETED: {
    title: 'Customer completed the checkout',
    detail: 'Waiting for the bank transfer to arrive.',
    tone: 'blue'
  },
  ORDER_FINALIZATION_STARTED: {
    title: 'Payment provider started its checks',
    detail: 'The payment is held while it is verified.',
    tone: 'amber'
  },
  PAYMENT_SUCCEEDED: { title: 'Payment received', detail: 'Credited to your balance.', tone: 'green' },
  CHECKOUT_SESSION_ABORTED: {
    title: 'Customer left before approving the payment',
    detail: 'Nothing was paid.',
    tone: 'neutral'
  },
  AML_REJECT: {
    title: 'Declined by the payment provider\'s checks',
    detail: 'Nothing was credited.',
    tone: 'red'
  },
  PAYMENT_NOT_SETTLED: {
    title: 'The bank transfer never arrived',
    detail: 'Nothing was credited.',
    tone: 'red'
  },
  ORDER_REFUND_INITIATED: { title: 'Refund started', tone: 'amber' },
  REFUND_UPDATED: { title: 'Refund completed', tone: 'neutral' }
}

const STATUS_HISTORY_PRESENTATION: Record<string, HistoryPresentation> = {
  processing: { title: 'Customer started the bank payment', tone: 'neutral' },
  waiting_for_payment: { title: 'Customer approved the payment', detail: 'Waiting for the transfer to arrive.', tone: 'blue' },
  authorized: { title: 'Payment authorised', tone: 'blue' },
  finalizing: { title: 'Payment provider started its checks', tone: 'amber' },
  paid: { title: 'Payment received', detail: 'Credited to your balance.', tone: 'green' },
  canceled: { title: 'Payment cancelled', detail: 'Nothing was paid.', tone: 'neutral' },
  cancelled: { title: 'Payment cancelled', detail: 'Nothing was paid.', tone: 'neutral' },
  failed: { title: 'Payment failed', detail: 'Nothing was credited.', tone: 'red' },
  in_refund: { title: 'Refund started', tone: 'amber' },
  partially_refunded: { title: 'Partially refunded', tone: 'amber' },
  refunded: { title: 'Refund completed', tone: 'neutral' },
  refund_failed: { title: 'Refund failed', tone: 'red' }
}

const humanise = (value: string): string => {
  const words = value.replace(/[_-]+/g, ' ').trim().toLowerCase()
  return words ? words[0].toUpperCase() + words.slice(1) : 'Status updated'
}

/**
 * Plain-language wording for one step of a payment's history. `credited` is
 * whether the payment has been added to the balance: a step that reports the
 * money as received only says it was credited when it was.
 */
export const openBankingHistoryPresentation = (
  entry: OpenBankingPaymentHistoryEntry,
  credited = true
): HistoryPresentation => {
  const reason = (entry.reason ?? '').trim().toUpperCase()
  if (!credited && normalise(entry.providerStatus) === 'paid') {
    return {
      title: 'Payment reported as received',
      detail: 'The payment provider reports the transfer as received.',
      tone: 'blue'
    }
  }
  if (reason && REASON_PRESENTATION[reason]) {
    return REASON_PRESENTATION[reason]
  }
  const byStatus = STATUS_HISTORY_PRESENTATION[normalise(entry.providerStatus)]
  if (byStatus) {
    // An unfamiliar reason is still worth showing: it is the provider's own word for why.
    return reason ? { ...byStatus, detail: byStatus.detail ?? humanise(reason) } : byStatus
  }
  return { title: humanise(entry.providerStatus), detail: reason ? humanise(reason) : undefined, tone: 'neutral' }
}

/**
 * The bank's name for display. When the provider only gave its own bank id
 * ("de-commerzbank"), the id is turned into a readable name.
 */
export const openBankingBankLabel = (bankName?: string, bankId?: string): string | undefined => {
  if (bankName?.trim()) return bankName.trim()
  const id = bankId?.trim()
  if (!id) return undefined
  const words = id.split(/[-_]+/).filter(Boolean)
  // Provider ids start with the bank's market: "de-", "gb-".
  if (words.length > 1 && words[0].length === 2) words.shift()
  return words.map(word => word[0].toUpperCase() + word.slice(1)).join(' ')
}

const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', GBP: '£', USD: '$' }

/** "€50.00", "£12.50", or "50.00 CHF" for a currency without a symbol here. */
export const formatFiatAmount = (amount: number | string | undefined, currency?: string): string => {
  const value = Number(amount ?? 0)
  const formatted = Number.isFinite(value)
    ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(amount ?? '')
  const code = (currency ?? '').toUpperCase()
  const symbol = CURRENCY_SYMBOLS[code]
  if (symbol) return `${symbol}${formatted}`
  return code ? `${formatted} ${code}` : formatted
}

const sessionIsClosed = (sessionStatus: string | undefined, expiresAt: string | undefined): boolean => {
  if (sessionStatus === 'cancelled' || sessionStatus === 'expired') return true
  if (sessionStatus === 'pending' && expiresAt) return new Date(expiresAt).getTime() < Date.now()
  return false
}

/**
 * The summary of a session's bank payment from the session itself. Used when
 * the API has not sent a summary (an older API build), so every surface can
 * still show one truthful status.
 */
export const openBankingSummaryFromSession = (session: ICheckoutSession): OpenBankingPaymentSummary | undefined => {
  const providerStatus = session.openBankingPaymentData?.status ??
    session.payment?.openBanking?.openBankingPaymentData?.status ??
    session.payment?.openBanking?.status
  if (!providerStatus) return undefined

  const history = session.openBankingPaymentData?.statusHistoryList ?? []
  const lastReason = history.length > 0 ? history[history.length - 1].reason : undefined
  return {
    checkoutSessionId: session.id,
    status: openBankingStatusFromProvider(
      providerStatus,
      lastReason,
      sessionIsClosed(session.sessionStatus, session.expiresAt),
      // The session does not say when the payment last changed: its own
      // creation is the earliest that can have been.
      history.length > 0 ? (history[history.length - 1].createdAt || session.createdAt) : session.createdAt
    ),
    providerStatus,
    statusReason: lastReason || undefined,
    amount: session.total,
    currency: session.currency ?? 'EUR',
    customerEmail: session.openBankingPaymentData?.shopper?.email ?? session.gateway?.openBanking?.email,
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
    expiresAt: session.expiresAt
  }
}

/** The drawer's view of a payment built from the session, for an older API build. */
export const openBankingViewFromSession = (session: ICheckoutSession): OpenBankingPaymentView | undefined => {
  const summary = openBankingSummaryFromSession(session)
  if (!summary) return undefined

  const paid = session.payment?.openBanking
  const paidData = paid?.openBankingPaymentData
  const address = paidData?.payerFinancialAddress ?? session.openBankingPaymentData?.payerFinancialAddress
  const historySource = paidData?.statusHistoryList ?? session.openBankingPaymentData?.statusHistoryList ?? []
  const history: OpenBankingPaymentHistoryEntry[] = historySource.length > 0
    ? historySource.map(entry => ({
      providerStatus: entry.currentStatus,
      reason: entry.reason || undefined,
      at: entry.createdAt || entry.updatedAt
    }))
    : [{ providerStatus: summary.providerStatus, at: summary.updatedAt }]

  const payer = {
    name: paidData?.senderName ?? address?.iban?.accountHolderName,
    iban: paidData?.senderIban ?? address?.iban?.iban,
    bic: paidData?.senderBic ?? address?.iban?.bic ?? undefined,
    sortCode: paidData?.senderSortCode,
    accountNumber: paidData?.senderAccountNumber,
    bankCode: paidData?.senderBankCode,
    bban: paidData?.senderBban
  }
  const hasPayer = Object.values(payer).some(value => !!value)
  const gross = paid?.onrampGrossAmount
  const metadata = session.metadata ?? []
  const metadataValue = (key: string): string | undefined => metadata.find(m => m.key === key)?.value || undefined

  return {
    ...summary,
    customerEmail: summary.customerEmail ?? paidData?.shopper?.email,
    history,
    credit: gross !== undefined && gross !== null
      ? {
        grossAmount: gross,
        feeAmount: paid?.onrampFee ?? 0,
        netAmount: paid?.onrampNetAmount ?? (Number(gross) - Number(paid?.onrampFee ?? 0)),
        network: paid?.onrampNetwork,
        token: paid?.onrampToken
      }
      : undefined,
    payer: hasPayer ? payer : undefined,
    bankName: paidData?.bankName ?? metadataValue('open_banking_bank_name'),
    bankId: paidData?.bankId ?? metadataValue('open_banking_bank_id'),
    bankMarket: paidData?.bankMarket ?? metadataValue('open_banking_bank_market') ?? metadataValue('market'),
    paymentScheme: paidData?.paymentScheme,
    bankStatementReference: paidData?.bankStatementReference || undefined,
    paymentReference: paidData?.paymentReference,
    endToEndId: paidData?.endToEndId,
    remittanceInformation: paidData?.remittanceInformation,
    customerPhone: paidData?.shopper?.phoneNumber || session.openBankingPaymentData?.shopper?.phoneNumber || undefined,
    paymentEventId: paid?.paymentEventId,
    metadata,
    refunds: session.refunds ?? [],
    refundable: ['paid', 'partiallyRefunded', 'refundFailed'].includes(summary.status)
  }
}

/**
 * The summary of a bank payment from one of its events. Older API builds
 * return the raw status events in search; this lets the search box group them
 * into the payment they belong to.
 */
export const openBankingSummaryFromEvent = (event: ManagedEvent): OpenBankingPaymentSummary | undefined => {
  const data = event.eventData.openBanking ?? event.eventData.payment
  const paymentData = data?.openBankingPaymentData
  const checkoutSessionId = data?.radomData?.checkoutSession?.checkoutSessionId
  if (!data || !paymentData || !checkoutSessionId) return undefined

  const providerStatus = paymentData.status ?? (event.eventType === 'payment' ? 'paid' : 'processing')
  const history = paymentData.statusHistoryList ?? []
  const lastReason = history.length > 0 ? history[history.length - 1].reason : undefined
  return {
    checkoutSessionId,
    status: openBankingStatusFromProvider(providerStatus, lastReason, false, event.eventDate),
    providerStatus,
    statusReason: lastReason || undefined,
    amount: data.amount,
    currency: data.paymentMethod?.token ?? 'EUR',
    customerEmail: paymentData.shopper?.email,
    createdAt: event.eventDate,
    updatedAt: event.eventDate
  }
}

/** Whether an event describes an open-banking payment (a status change, or the paid event). */
export const isOpenBankingPaymentEvent = (event: ManagedEvent): boolean =>
  event.eventType === 'openBanking' ||
  (event.eventType === 'payment' &&
    event.eventData.payment?.paymentMethod?.network === 'Fiat' &&
    !!event.eventData.payment?.openBankingPaymentData)
